import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core'
import { forkJoin, of, Subject } from 'rxjs'
import { filter, first, mergeMap, take, takeUntil, tap } from 'rxjs/operators'
import { fadeIn } from '../../../animations/fadeIn'
import { KeySystemNavConfig } from '../../../configuration/key.system.nav.config'
import { KeySystem, Structure, StructureDetails } from '../../../models/dev.test.models'
import { ErrorService } from '../../../services/error.service'
import { KeyingAuthService } from '../../../services/keying.auth.service'
import { KeyingDataSetsService, StructureDataSets } from '../../../services/keying.data.sets.service'
import { KeyingStructureService } from '../../../services/keying.structure.service'
import { KeyingSystemService } from '../../../services/keying.system.service'
import { LoadingService } from '../../../services/loading.service'
import { User } from '@overtur-monorepo/keying-user'
import { DealerMasterKey, DealerMasterKeyRequest, Keyway, ToastService } from '@overtur-monorepo/keying-shared'
import { DealerMasterKeyService } from '@overtur-monorepo/keying-hierarchy'
import { MasterKeyUpdateService } from '../../../services/masterkey.update.service'
import { AppRoutingConfig } from '../../../configuration/app.routing.config'
import { Router } from '@angular/router'
import { DeactivatableComponent } from '../../../guards/unsavedChangesGuard'
import { DialogService } from '../../../services/dialog.service'
import { UnsavedChangesService } from '../../../services/unsaved.changes.service'

@Component({
    selector: 'app-dealer-masterkey',
    templateUrl: './dealer-masterkey.component.html',
    styleUrls: ['./dealer-masterkey.component.scss'],
    animations: [fadeIn],
    standalone: false
})
export class DealerMasterKeyComponent implements OnInit, OnDestroy, DeactivatableComponent {
  destroy$ = new Subject()

  structure: Structure
  keySystem: KeySystem
  keyways: Keyway[]
  user: User
  loading
  dealerMasterKeys: DealerMasterKey[] = []
  disableUpdate = false
  isUpdatingSystem = false
  blocked = false

  saveDisabled = false
  saveAndContinueDisabled = true

  initialLoad = true

  dealerSpecsSaveFormData: DealerMasterKeyRequest[] = []
  dealerSpecsSaveAndContinueFormData: DealerMasterKeyRequest[] = []

  @Output() dealererror = new EventEmitter<any>()

  constructor(
    private keyingAuthService: KeyingAuthService,
    private keyingStructureService: KeyingStructureService,
    private keyingSystemService: KeyingSystemService,
    private errorService: ErrorService,
    private keySystemNavConfig: KeySystemNavConfig,
    private keyingDataSetsService: KeyingDataSetsService,
    private loadingService: LoadingService,
    private dealerMasterKeyService: DealerMasterKeyService,
    private masterKeyUpdateService: MasterKeyUpdateService,
    private toastService: ToastService,
    private dialogService: DialogService,
    private router: Router,
    private unsavedChangesService: UnsavedChangesService
  ) {}

  /* istanbul ignore next */
  ngOnInit(): void {
    this.keyingDataSetsService.structureDetails$
      .pipe(takeUntil(this.destroy$))
      .subscribe((details: StructureDetails) => {
        if (details && (details.mecsInProgress || details.publishingInProgress || details?.pinningGenerationInprogress)) {
          this.router.navigate([AppRoutingConfig.structuresUrl])
        }
      })
    this.loadKeyingData()
    this.dealerMasterKeyService.clearDealerSpecFormData()
    this.dealerMasterKeyService.clearDealerSpecSaveAndContinueFormData()
    this.dealerMasterKeyService.getDealerSpecFormData().subscribe((res) => {
      this.dealerSpecsSaveFormData = res

      if (this.dealerSpecsSaveFormData.length === 0) {
        this.saveDisabled = true
      } else {
        // this.saveDisabled = this.dealerSpecsSaveFormData.some((masterKey) => masterKey.formValid === false)
        this.saveDisabled = false
        this.initialLoad = false
        this.saveAndContinueDisabled = !this.saveDisabled
      }
    })

    this.dealerMasterKeyService.getDealerSpecSaveAndContinueFormData().subscribe((res) => {
      this.dealerSpecsSaveAndContinueFormData = res.filter((masterKey) => masterKey.masterKeyId)

      if (this.dealerSpecsSaveAndContinueFormData.length === 0) {
        this.saveAndContinueDisabled = true
      } else {
        this.saveAndContinueDisabled = this.dealerSpecsSaveAndContinueFormData.some(
          (masterKey) => masterKey.formValid === false
        )
        this.saveDisabled = !this.saveAndContinueDisabled
      }
    })
  }

  /* istanbul ignore next */
  hasUnsavedChanges(): boolean {
    if (!this.initialLoad) {
      if (!this.saveDisabled) {
        let condition = true
        this.unsavedChangesService.setUnsavedChangesStatus(condition)
        return condition
      } else {
        this.unsavedChangesService.setUnsavedChangesStatus(false)
        return false
      }
    }
  }

  /* istanbul ignore next */
  canDeactivate() {
    if (!this.hasUnsavedChanges()) {
      return true
    } else {
      this.keySystemNavConfig.setNav('Dealer', this.structure, this.keySystem)
      return false
    }
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  /* istanbul ignore next */
  loadKeyingData() {
    this.loadUser()
    this.loadStructureDataSets()
    this.loadStructureAndKeySystem()
    this.updateLoadingStatus()
  }

  /* istanbul ignore next */  
  loadStructureAndKeySystem() {
    this.keyingStructureService.structure$
      .pipe(
        filter((structure) => !!structure),
        take(1),
        mergeMap((structure: Structure) => {
          this.structure = structure
          return this.keyingSystemService.keySystem$.pipe(filter((keySystem) => !!keySystem && !!keySystem.masterKeysKeying), 
          first(),
        )
        }),
        mergeMap((keySystem) => {
          this.keySystem = keySystem

          if (this.keySystem?.masterKeysKeying?.keySetSymbol === 'DEALER SPEC') {
            this.keySystem.masterKeysKeying.disableTMKToggling = true
          }

          if (this.structure && this.keySystem) {
            this.keySystemNavConfig.setNav('Dealer', this.structure, this.keySystem)
            return forkJoin([of(keySystem), this.getDealerMasterKeys()])
          }
          return forkJoin([of(keySystem), of(null)])
        })
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([keySystem, dealerSpec]) => {
          this.keySystem = keySystem
          
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  loadUser() {
    this.keyingAuthService.keyingUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (!user) {
        return
      }
      this.user = user
    })
  }

  /* istanbul ignore next */
  loadStructureDataSets() {
    this.keyingDataSetsService.structureDataSets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((structureDataSets: StructureDataSets) => {
        if (!structureDataSets) {
          return
        }
        this.keyways = structureDataSets.keyways
      })
  }

  /* istanbul ignore next */
  updateLoadingStatus() {
    this.loadingService.loading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      this.loading = loading
    })
  }

  /* istanbul ignore next */
  getDealerMasterKeys() {
    return this.dealerMasterKeyService
      .getDealerMasterKeys(this.structure.id, this.keySystem.id)
      .pipe(take(1), tap((dealerMasterKeys) => {
        this.dealerMasterKeys = dealerMasterKeys
      }))
      
  }

  /* istanbul ignore next */
  resetDealerKeySystem() {
    this.dealerMasterKeyService
      .resetDealerKeySystem(this.structure.id, this.keySystem.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          this.errorService.confirm('Reset success')
          /* Get Structure Details Everytime DealerKeySystem Updated */
          this.keyingDataSetsService.loadStructureDetails(this.structure.id)
          this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
          this.router.navigate([AppRoutingConfig.getDealerKeySystemUrl(this.structure.id, this.keySystem.id)])
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  updateDealerKeySystem() {
    this.loadingService.set({ isLoading: true, message: 'Updating Bittings' })
    this.isUpdatingSystem = true
    this.dealerMasterKeyService
      .updateDealerKeySystem(this.structure.id, this.keySystem.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.isUpdatingSystem = false
          this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
          if (this.blocked) {
            this.blocked = false
            this.updateDealerKeySystem()
          }
        },
        (error) => {
          this.errorService.throw(error)
          this.loadingService.set({ isLoading: false, message: '' })
          this.isUpdatingSystem = false
        }
      )
  }

  /* istanbul ignore next */
  patchItem(patchData) {
    this.masterKeyUpdateService.patchItem(this.structure, this.keySystem, patchData)
    console.log('patch')
  }

  /* istanbul ignore next */
  handleDealerError(error: any) {
    this.errorService.throw(error)
  }

  /* istanbul ignore next */
  saveDealerSpec(event?) {
    if (event) {
      event.preventDefault()
    }
    const request = this.dealerSpecsSaveFormData
    this.dealerMasterKeyService.setDealerSpecFormData(request)
    this.loadingService.set({ isLoading: true, message: 'Saving Keys' })
    this.dealerMasterKeyService
      .saveDealerSpecs(this.structure.id, this.keySystem.id, request)
      .pipe(take(1))
      .subscribe({
        next: () => this.updateDealerSpecResult(),
        error: (error) => {
          this.errorService.throw(error)
          this.loadingService.set({ isLoading: false, message: '' })
        }
      })
  }

  /* istanbul ignore next */
  updateDealerSpecResult() {
    this.saveDisabled = true
    this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)

    this.dealerMasterKeys = []
    this.dealerMasterKeyService
      .getDealerMasterKeys(this.structure.id, this.keySystem.id)
      .pipe(take(1))
      .subscribe((res) => {
        this.dealerMasterKeys = res
      })

    this.loadingService.set({ isLoading: false, message: '' })
    this.errorService.confirm('Keys Saved')
    this.initialLoad = true
    this.dealerMasterKeyService.clearDealerSpecFormData()
    this.dealerMasterKeyService.clearDealerSpecSaveAndContinueFormData()
    this.hasUnsavedChanges()
  }

  /* istanbul ignore next */
  saveAndContinueDealerSpec() {
    const request = this.dealerSpecsSaveAndContinueFormData.map((dealerKey) => {
      delete dealerKey.element
      return dealerKey
    })

    this.loadingService.set({ isLoading: true, message: 'Validating Keys' })
    this.dealerMasterKeyService
      .validateDealerSpecs(this.structure.id, this.keySystem.id, request)
      .pipe(take(1))
      .subscribe(
        (res) => {
          this.loadingService.set({ isLoading: false, message: '' })
          if (res.length < 1) {
            this.loadingService.set({ isLoading: true, message: 'Saving Keys' })
            this.dealerMasterKeyService
              .saveandProcessDealerSpecs(this.structure.id, this.keySystem.id, request)
              .pipe(take(1))
              .subscribe(
                () => {
                  this.loadingService.set({ isLoading: false, message: '' })
                  this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
                  this.dealerMasterKeyService.clearDealerSpecFormData()
                  this.dealerMasterKeyService.clearDealerSpecSaveAndContinueFormData()
                  this.errorService.confirm('Keys Saved')
                },
                (error) => {
                  this.errorService.throw(error)
                  this.loadingService.set({ isLoading: false, message: '' })
                }
              )
          } else {
            this.loadingService.set({ isLoading: false, message: '' })
            let errorMessages = ''
            res.forEach((error) => {
              errorMessages += error.message + '.' + '\n'
            })
            this.errorService.showError(errorMessages)
          }
        },
        (error) => {
          this.errorService.throw(error)
          this.loadingService.set({ isLoading: false, message: '' })
        }
      )
  }

  /* istanbul ignore next */
  importFile(file) {
    this.loadingService.set({ isLoading: true, message: 'Importing' })
    this.dealerMasterKeyService.importDealerSpec(this.structure.id, this.keySystem.id, file).subscribe({
        next: () => this.updateDealerSpecResult(),
        error: (error) => {
            this.errorService.throw(error)
            this.loadingService.set({ isLoading: false, message: '' })
        }
    })
  }
}
